import React from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation, Trans } from 'react-i18next';
import Card from '@material-ui/core/Card';
import CardMedia from '@material-ui/core/CardMedia';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import FavoriteIcon from '@material-ui/icons/Favorite';
import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import { Resource } from 'types/schema-generated-types';

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'row',
      minHeight: 210,
      '&:hover': {
        boxShadow: '0 1px 8px 4px rgba(0, 0, 0, 0.20)',
        transform: 'scale(1.01, 1.01)'
      }
    },
    thumbnail: {
      width: 150
    },
    details: {
      padding: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      flex: 1
    },
    header: {
      display: 'flex'
    },
    description: {
      marginTop: theme.spacing(1),
      display: 'flex',
      flex: 1
    },
    tags: {
      marginTop: theme.spacing(1),
      display: 'flex',
      flexWrap: 'wrap',
      '& > *': {
        marginRight: theme.spacing(0.5),
        cursor: 'pointer',
        borderRadius: 0
      }
    },
    favoriteContainer: {
      alignItems: 'flex-end'
    },
    favorite: {
      color: theme.palette.primary.main
    }
  };
});

type ResourceCardProps = {
  icon: string;
  thumbnail: string;
  handleCardClick: () => void;
  handleFavoriteClick: () => void;
} & Partial<Resource>;

const ResourceCard: React.FC<ResourceCardProps> = ({
  title,
  description,
  productFamily,
  grades,
  type,
  thumbnail,
  favorite,
  handleCardClick,
  handleFavoriteClick
}) => {
  const classes = useStyles();
  const { t } = useTranslation('TileCard');

  const handleFavoriteIconClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.stopPropagation();
    handleFavoriteClick();
  };

  return (
    <Card className={classes.root} onClick={handleCardClick}>
      <CardMedia className={classes.thumbnail} image={thumbnail} title={title} />
      <CardContent className={classes.details}>
        <div className={classes.header}>
          <Typography variant="h3">
            <Trans>{title}</Trans>
          </Typography>
        </div>
        <div className={classes.description}>
          <Typography variant="body1">
            <Trans>{description}</Trans>
          </Typography>
        </div>
        <div>
          <div className={classes.tags}>
            <Chip label={type?.title} variant="outlined" />
            {productFamily && <Chip label={productFamily?.name} variant="outlined" />}
            {grades && <Chip label={`Grade(s) ${grades}`} variant="outlined" />}
          </div>
        </div>
      </CardContent>
      <CardActions disableSpacing className={classes.favoriteContainer}>
        <IconButton aria-label={t('Add to favorites')} onClick={handleFavoriteIconClick}>
          <FavoriteIcon className={favorite ? classes.favorite : ''} />
        </IconButton>
      </CardActions>
    </Card>
  );
};

export default ResourceCard;
